import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const AgentVetting = () => {
  const { user, userProfile, loading: authLoading, sessionChecked } = useAuth();
  const navigate = useNavigate();
  const [agentProfile, setAgentProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [selectedFiles, setSelectedFiles] = useState({});
  
  // Create refs for each document type
  const fileInputRefs = useRef({});

  useEffect(() => {
    document.title = 'Agent Vetting - HoliYay';
  }, []);

  // Check authentication and authorization
  useEffect(() => {
    if (!sessionChecked || authLoading) return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (userProfile?.role !== 'agent') {
      navigate('/', { replace: true });
      return;
    }

    fetchAgentProfile();
  }, [user, userProfile, authLoading, sessionChecked, navigate]);

  const fetchAgentProfile = async () => {
    try {
      setLoading(true);
      
      console.log('[AgentVetting] Fetching profile for user:', user?.id);
      
      // Step 1: Fetch agent profile using maybeSingle() to handle 0 or >1 rows
      let { data: profile, error: profileError } = await supabase
        ?.from('agent_profiles')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.maybeSingle();

      // Step 2: If profile is null (no row exists), create it automatically
      if (!profile && !profileError) {
        console.log('[AgentVetting] No profile found, creating one...');
        
        const { data: newProfile, error: upsertError } = await supabase
          ?.from('agent_profiles')
          ?.upsert({
            user_id: user?.id,
            status: 'pending',
            updated_at: new Date()?.toISOString()
          }, { 
            onConflict: 'user_id',
            ignoreDuplicates: false 
          })
          ?.select()
          ?.maybeSingle();

        if (upsertError) {
          console.error('[AgentVetting] Profile creation error:', upsertError);
          throw upsertError;
        }

        profile = newProfile;
        console.log('[AgentVetting] Profile created successfully:', profile);
      }

      // Step 3: If still no profile (edge case: multiple rows exist), fetch the newest one
      if (!profile) {
        console.log('[AgentVetting] Handling potential multiple rows...');
        
        const { data: latestProfile, error: latestError } = await supabase
          ?.from('agent_profiles')
          ?.select('*')
          ?.eq('user_id', user?.id)
          ?.order('created_at', { ascending: false })
          ?.limit(1)
          ?.maybeSingle();

        if (latestError) {
          console.error('[AgentVetting] Latest profile fetch error:', latestError);
          throw latestError;
        }

        profile = latestProfile;
      }

      // Step 4: Final validation - if still no profile, throw error
      if (!profile) {
        throw new Error('Unable to load or create agent profile. Please contact support.');
      }

      if (profileError) {
        console.error('[AgentVetting] Profile fetch error:', profileError);
        throw profileError;
      }

      console.log('[AgentVetting] Profile loaded:', profile);
      setAgentProfile(profile);

      // Redirect if already approved
      if (profile?.status === 'approved') {
        navigate('/agent-dashboard', { replace: true });
        return;
      }

      // Redirect if rejected
      if (profile?.status === 'rejected') {
        navigate('/agent-rejection-notice', { replace: true });
        return;
      }

      // Fetch uploaded documents
      const { data: docs, error: docsError } = await supabase
        ?.from('agent_documents')
        ?.select('*')
        ?.eq('agent_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (docsError) {
        console.error('[AgentVetting] Documents fetch error:', docsError);
        throw docsError;
      }

      console.log('[AgentVetting] Documents fetched:', docs);
      setDocuments(docs || []);
    } catch (error) {
      console.error('[AgentVetting] Error fetching profile:', error);
      setUploadError(`Failed to load profile: ${error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event, documentType) => {
    const file = event?.target?.files?.[0];
    if (!file) {
      console.log('[AgentVetting] No file selected');
      return;
    }

    console.log('[AgentVetting] File selected:', file?.name, 'Type:', documentType);
    
    // Store selected file in state
    setSelectedFiles(prev => ({
      ...prev,
      [documentType]: file
    }));
    
    setUploadError('');
    setUploadSuccess('');
  };

  const handleFileUpload = async (documentType) => {
    const file = selectedFiles?.[documentType];
    
    if (!file) {
      setUploadError('Please select a file first');
      return;
    }

    if (!user?.id) {
      setUploadError('Please log in to upload documents');
      return;
    }

    setUploading(true);
    setUploadError('');
    setUploadSuccess('');

    try {
      console.log('[AgentVetting] Starting upload for:', documentType);
      
      // Validate file size (max 5MB)
      if (file?.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      // Sanitize filename
      const timestamp = Date.now();
      const fileExt = file?.name?.split('.')?.pop()?.toLowerCase();
      const sanitizedFileName = file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `agents/${user?.id}/${documentType}/${timestamp}_${sanitizedFileName}`;

      console.log('[AgentVetting] Uploading to path:', filePath);

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase
        ?.storage
        ?.from('agent-documents')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('[AgentVetting] Upload error:', uploadError);
        throw uploadError;
      }

      console.log('[AgentVetting] File uploaded successfully:', uploadData);

      // UPSERT document record - replaces existing record if (agent_id, document_type) exists
      const upsertPayload = {
        agent_id: user?.id,
        document_type: documentType,
        file_name: file?.name,
        file_path: filePath,
        file_size: file?.size,
        mime_type: file?.type,
        status: 'pending',
        updated_at: new Date()?.toISOString()
      };

      console.log('[AgentVetting] Upserting document record:', upsertPayload);

      const { data: upsertData, error: upsertError } = await supabase
        ?.from('agent_documents')
        ?.upsert(upsertPayload, { 
          onConflict: 'agent_id,document_type',
          ignoreDuplicates: false
        })
        ?.select()
        ?.single();

      if (upsertError) {
        console.error('[AgentVetting] Database upsert error:', {
          error: upsertError,
          payload: upsertPayload,
          message: upsertError?.message,
          details: upsertError?.details,
          hint: upsertError?.hint
        });
        // Try to clean up uploaded file
        await supabase?.storage?.from('agent-documents')?.remove([filePath]);
        throw upsertError;
      }

      console.log('[AgentVetting] Document record upserted successfully:', upsertData);

      setUploadSuccess(`${documentType?.replace(/_/g, ' ')} uploaded successfully`);
      
      // Clear selected file
      setSelectedFiles(prev => {
        const updated = { ...prev };
        delete updated?.[documentType];
        return updated;
      });
      
      // Clear file input
      if (fileInputRefs?.current?.[documentType]) {
        fileInputRefs.current[documentType].value = '';
      }
      
      // Refresh documents list to show updated status
      await fetchAgentProfile();
      
      // Clear success message after 5 seconds
      setTimeout(() => setUploadSuccess(''), 5000);
    } catch (error) {
      console.error('[AgentVetting] Upload error:', error);
      setUploadError(error?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const getDocumentStatus = (docType) => {
    const doc = documents?.find(d => d?.document_type === docType);
    if (!doc) return { status: 'not_uploaded', label: 'Not Uploaded', color: 'text-muted-foreground' };
    if (doc?.status === 'approved') return { status: 'approved', label: 'Approved', color: 'text-green-600' };
    if (doc?.status === 'rejected') return { status: 'rejected', label: 'Rejected', color: 'text-red-600' };
    return { status: 'pending', label: 'Pending Review', color: 'text-yellow-600' };
  };

  const requiredDocuments = [
    {
      type: 'incorporation_certificate',
      title: 'Incorporation Certificate',
      description: 'Certificate of incorporation or business registration document'
    },
    {
      type: 'travel_license',
      title: 'Travel Authority License (ATOL/ABTA)',
      description: 'Valid ATOL, ABTA or equivalent travel authority certification'
    },
    {
      type: 'professional_insurance',
      title: 'Professional Insurance',
      description: 'Professional indemnity and public liability insurance certificate'
    },
    {
      type: 'terms_conditions',
      title: 'Terms & Conditions',
      description: 'Your company\'s terms and conditions document'
    },
    {
      type: 'privacy_policy',
      title: 'Privacy Policy',
      description: 'Your company\'s privacy policy document'
    }
  ];

  if (loading || authLoading || !sessionChecked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="Lock" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Please Log In</h2>
              <p className="text-muted-foreground mb-6">
                You need to be logged in to access the agent vetting page.
              </p>
              <Button onClick={() => navigate('/login')}>Go to Login</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header Section */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Icon name="Clock" size={32} className="text-yellow-600" />
                </div>
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-foreground mb-2">Application Under Review</h1>
                <p className="text-muted-foreground mb-4">
                  Thank you for registering as a travel agent with HoliYay. Your application is currently being reviewed by our team.
                </p>
                <div className="flex items-center space-x-2 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3">
                  <Icon name="Info" size={16} className="text-yellow-600" />
                  <p className="text-sm text-yellow-800">
                    <strong>Current Status:</strong> Pending Approval
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Document Upload Section */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-2">Required Documents</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Please upload the following documents to complete your verification process. Our team will review them as soon as possible.
            </p>

            {uploadSuccess && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-green-600" />
                  <p className="text-sm text-green-800">{uploadSuccess}</p>
                </div>
              </div>
            )}

            {uploadError && (
              <div className="mb-6 bg-error/10 border border-error/20 rounded-lg p-4">
                <div className="flex flex-col space-y-2">
                  <div className="flex items-start space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-error font-medium mb-1">Upload Error</p>
                      <p className="text-sm text-error">{uploadError}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {requiredDocuments?.map((doc) => {
                const status = getDocumentStatus(doc?.type);
                const selectedFile = selectedFiles?.[doc?.type];
                const hasFile = !!selectedFile;
                
                return (
                  <div key={doc?.type} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-medium text-foreground">{doc?.title}</h3>
                          <span className={`text-xs font-medium ${status?.color}`}>
                            {status?.label}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{doc?.description}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        ref={el => fileInputRefs.current[doc?.type] = el}
                        type="file"
                        id={`file-${doc?.type}`}
                        className="hidden"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => handleFileSelect(e, doc?.type)}
                        disabled={uploading}
                      />
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRefs?.current?.[doc?.type]?.click()}
                        disabled={uploading}
                        iconName="Upload"
                        iconPosition="left"
                      >
                        Choose File
                      </Button>
                      
                      {hasFile && (
                        <>
                          <span className="text-sm text-muted-foreground truncate max-w-xs">
                            {selectedFile?.name}
                          </span>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleFileUpload(doc?.type)}
                            disabled={uploading}
                            loading={uploading}
                            iconName="Upload"
                            iconPosition="left"
                          >
                            {uploading ? 'Uploading...' : 'Upload'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 bg-muted/50 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="FileText" size={16} className="text-muted-foreground mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="mb-1">Accepted formats: PDF, DOC, DOCX</p>
                  <p>Maximum file size: 5MB per document</p>
                </div>
              </div>
            </div>
          </div>

          {/* What Happens Next Section */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">What Happens Next?</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-orange-600">1</span>
                </div>
                <div>
                  <h3 className="font-medium text-foreground mb-1">Document Review</h3>
                  <p className="text-sm text-muted-foreground">
                    Our verification team will review your submitted documents within 2-3 business days.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-orange-600">2</span>
                </div>
                <div>
                  <h3 className="font-medium text-foreground mb-1">Verification Process</h3>
                  <p className="text-sm text-muted-foreground">
                    We'll verify your credentials, licenses, and business information to ensure compliance.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-orange-600">3</span>
                </div>
                <div>
                  <h3 className="font-medium text-foreground mb-1">Approval & Access</h3>
                  <p className="text-sm text-muted-foreground">
                    Once approved, you'll receive an email notification and gain full access to the agent dashboard.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Support Section */}
          <div className="bg-muted/50 rounded-lg p-6">
            <div className="text-center">
              <Icon name="HelpCircle" size={32} className="mx-auto mb-3 text-muted-foreground" />
              <h3 className="font-semibold text-foreground mb-2">Need Assistance?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Our support team is here to help you through the verification process.
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm">
                <a
                  href="mailto:support@holiyay.com"
                  className="flex items-center space-x-2 text-orange-600 hover:text-orange-700"
                >
                  <Icon name="Mail" size={16} />
                  <span>support@holiyay.com</span>
                </a>
                <a
                  href="tel:+442012345678"
                  className="flex items-center space-x-2 text-orange-600 hover:text-orange-700"
                >
                  <Icon name="Phone" size={16} />
                  <span>+44 20 1234 5678</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AgentVetting;